import { ReactNode } from 'react';
import { paginationUtils } from 'v5/api/_common/query/pagination';
import { WithPagination } from 'v5/api/_common/query/queryUtils';

function hasPagination<Q>(query: Q): query is Q & WithPagination {
  return (
    typeof query === 'object' &&
    query !== null &&
    'page' in query &&
    typeof (query as WithPagination).page?.page === 'number' &&
    typeof (query as WithPagination).page?.pageSize === 'number'
  );
}

/**
 * When the list request succeeded with 0 items but the URL page is past the
 * last page for `totalCount` (stale pagination), prefer the out-of-range empty
 * state over the feature's "no entities" empty state.
 */
export function resolveEntityListEmptyContent<Q>(args: {
  emptyState: ReactNode;
  query: Q;
  totalCount: number | undefined;
  outOfRangeEmptyState: (goToLastPage: () => void) => ReactNode;
  onQueryChange: (query: Q) => void;
}): ReactNode {
  const { emptyState, query, totalCount, outOfRangeEmptyState, onQueryChange } =
    args;

  if (typeof totalCount !== 'number' || !hasPagination(query)) {
    return emptyState;
  }

  if (!paginationUtils.isPageOutOfRange(query.page, totalCount)) {
    return emptyState;
  }

  const lastPageIndex = paginationUtils.getLastPageIndex(
    query.page,
    totalCount
  );
  return outOfRangeEmptyState(() =>
    onQueryChange({
      ...query,
      page: {
        ...query.page,
        page: lastPageIndex
      }
    })
  );
}
